import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import queryString from 'query-string'
import styles from './styles.module.scss'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import WorkplaceSidebar from './components/WorkplaceSidebar'
import CommonHeader from './components/CommonHeader'
import SidebarMenu from './components/SidebarMenu'
import MobileHeader from './components/MobileHeader'
import Display from './components/Display'
import LeadershipTeam from './components/LeadershipTeam'
import ManageInvites from './components/ManageInvites'
import InviteMembers from './components/InviteMembers'
import { checkNested } from 'srcReact/helpers/validator'
import Members from './components/Members'
import Groups from './components/Groups'
import { urlSearchParams } from 'srcReact/helpers/utils'
import { workplaceSettingsActions } from 'srcReact/WorkplaceSettingsPage'
import RightsPermissions from './components/RightsPermissions'
import { redirectToPage } from 'srcReact/helpers/history'

const WorkplaceSettingsPage = (props) => {
  const { t } = useTranslation()

  const query = queryString.parse(location.search)

  const dispatch = useDispatch()
  const {
    common: { isNative, isDesktop },
    organization: { current_organization },
    workplaceSettings: { workplace_list, display, workplace_members }
  } = useSelector((state) => state)
  const [workplaceId, setWorkplaceId] = useState(props.match.params.workplaceId)
  const [selectedWorkplace, setSelectedWorkplace] = useState({ name: '', id: 0, color: '', role: '', initials: '' })
  const [activeItem, setActiveItem] = useState(t('workplaceSettings.title'))

  const freeAccessSections = ['link_5', 'link_4']
  const availableSections = ['link_1', 'link_2', 'link_3', 'link_4', 'link_5', 'link_6', 'link_7']

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeItem])
  useEffect(() => {
    if (props.match.params.workplaceId !== workplaceId) {
      setWorkplaceId(props.match.params.workplaceId)
    }
  }, [props.match.params.workplaceId])
  useEffect(() => {
    // nativeapp
    if (isNative) {
      dispatch(workplaceSettingsActions.getWorkplaceList(urlSearchParams('organization_id')))
      return
    }

    if (checkNested(current_organization, 'info', 'id')) {
      dispatch(workplaceSettingsActions.getWorkplaceList(current_organization.info.id))
    }
  }, [current_organization && current_organization.info && current_organization.info.id])
  useEffect(() => {
    if (workplaceId > 0 && workplace_list?.length > 0) {
      const workplace = workplace_list?.find((w) => w?.id == workplaceId)
      if (workplace) {
        setSelectedWorkplace(workplace)
      } else {
        redirectToPage(`/0/myhome`, isNative)
      }
    }
  }, [workplaceId, workplace_list])
  useEffect(() => {
    if (selectedWorkplace?.id > 0) {
      if (selectedWorkplace?.role?.includes('workplace-manager')) {
        if (selectedWorkplace?.id !== display?.id) {
          dispatch(workplaceSettingsActions.getWorkplaceSettings(selectedWorkplace?.id))
        }
        getActiveItem(
          query?.section && availableSections?.includes(query.section) ? query.section : isDesktop ? 'link_1' : ''
        )
      } else {
        getActiveItem(
          query?.section && freeAccessSections?.includes(query.section) ? query.section : isDesktop ? 'link_5' : ''
        )
      }
    }
  }, [selectedWorkplace])

  useEffect(() => {
    if (query?.section) {
      getActiveItem(query.section)
    }
  }, [query?.section])

  const getActiveItem = (type) => {
    switch (type) {
      case 'link_1':
        setActiveItem(t('workplaceSettings.display.title'))
        break
      case 'link_2':
        setActiveItem(t('global.inviteMembers'))
        break
      case 'link_3':
        setActiveItem(t('workplaceSettings.manageInvites.title'))
        break
      case 'link_4':
        setActiveItem(t('workplaceSettings.groups.title'))
        break
      case 'link_5':
        setActiveItem(t('workplaceSettings.leadershipTeam.title'))
        break
      case 'link_6':
        setActiveItem(t('global.members'))
        break
      case 'link_7':
        setActiveItem(t('workplaceSettings.rightsPermissions.title'))
        break
      default:
        setActiveItem(t('workplaceSettings.title'))
        break
    }
  }
  const renderMainContent = () => {
    const commonProps = {
      selectedWorkplace
    }
    switch (activeItem) {
      case t('workplaceSettings.display.title'):
        return <Display {...commonProps} />
      case t('global.inviteMembers'):
        return <InviteMembers {...commonProps} />
      case t('workplaceSettings.manageInvites.title'):
        return <ManageInvites {...commonProps} />
      case t('workplaceSettings.groups.title'):
        return <Groups {...commonProps} />
      case t('workplaceSettings.leadershipTeam.title'):
        return <LeadershipTeam {...commonProps} />
      case t('global.members'):
        return <Members {...commonProps} />
      case t('workplaceSettings.rightsPermissions.title'):
        return <RightsPermissions {...commonProps} />
    }
  }
  const handleSelectedWorkplace = (id) => {
    setWorkplaceId(id)
    setSelectedWorkplace(workplace_list?.find((w) => w?.id == id))
  }
  const handleActiveItem = (item) => {
    if (item === t('workplaceSettings.leadershipTeam.title')) {
      redirectToPage(`/0/workplace/settings/${workplaceId}?section=link_5`)
    } else if (item === t('global.members')) {
      redirectToPage(`/0/workplace/settings/${workplaceId}?section=link_6`)
    } else if (item === t('workplaceSettings.groups.title')) {
      redirectToPage(`/0/workplace/settings/${workplaceId}?section=link_4`)
    } else {
      setActiveItem(item)
    }
  }

  let listItems = []
  if (selectedWorkplace?.id > 0) {
    if (selectedWorkplace?.role?.includes('workplace-manager')) {
      listItems = [
        {
          heading: t('workplaceSettings.title'),
          items: [
            t('workplaceSettings.display.title'),
            t('global.inviteMembers'),
            t('workplaceSettings.manageInvites.title'),
            t('workplaceSettings.groups.title'),
            t('workplaceSettings.leadershipTeam.title'),
            t('global.members'),
            t('workplaceSettings.rightsPermissions.title')
          ],
          counts: [0, 0, 0, 0, 0, workplace_members?.members_stats?.total, 0],
          icons: ['display', 'send', 'add-contact', 'groups24', 'leadership', 'contact', 'handshake']
        }
      ]
    } else {
      listItems = [
        {
          heading: t('workplaceSettings.title'),
          items: [t('workplaceSettings.leadershipTeam.title'), t('global.members')],
          counts: [0, workplace_members?.members_stats?.total],
          icons: ['leadership', 'contact']
        }
      ]
    }
  }
  const isMobileMainPage = activeItem === t('workplaceSettings.title')

  return (
    <PageContainer
      pageClassName={styles.workplaceSettingsWrapper}
      pageTitle={t('workplaceSettings.title')}
      withFooter={false}>
      {!isDesktop && (
        <MobileHeader
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          selectedWorkplace={selectedWorkplace}
          handleSelectedWorkplace={handleSelectedWorkplace}
          isMobileMainPage={isMobileMainPage}
        />
      )}

      {isDesktop && (
        <div className={styles.workplaceSidebar}>
          <WorkplaceSidebar
            workplaceId={workplaceId}
            selectedWorkplace={selectedWorkplace}
            handleSelectedWorkplace={handleSelectedWorkplace}
          />
        </div>
      )}

      {(isDesktop || isMobileMainPage) && (
        <div className={styles.sidebarMenu}>
          {!isDesktop && (
            <CommonHeader
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              selectedWorkplace={selectedWorkplace}
              isMobileMainPage={isMobileMainPage}
            />
          )}
          <SidebarMenu
            listItems={listItems}
            activeItem={activeItem}
            setActiveItem={handleActiveItem}
            selectedWorkplace={selectedWorkplace}
          />
        </div>
      )}

      <div className={styles.mainContent}>
        {!isMobileMainPage && (
          <CommonHeader
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            selectedWorkplace={selectedWorkplace}
            isMobileMainPage={isMobileMainPage}
          />
        )}
        {activeItem !== t('workplaceSettings.title') && <main className={styles.content}>{renderMainContent()}</main>}
      </div>
    </PageContainer>
  )
}

export { WorkplaceSettingsPage }
